import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { FileText, Briefcase, DollarSign, TrendingUp, Calendar } from 'lucide-react';

const SalesPipelineDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    pendingEstimates: { count: 0, value: 0 },
    activeJobs: { count: 0, value: 0 },
    outstandingInvoices: { count: 0, value: 0 },
    mtdRevenue: 0,
    ytdRevenue: 0,
    monthlyTrend: []
  });

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const yearStart = new Date(now.getFullYear(), 0, 1);

      // Pending Estimates
      const { data: estimates } = await supabase
        .from('estimates')
        .select('total_amount')
        .in('status', ['Draft', 'Sent']);

      // Active Jobs
      const { data: jobs } = await supabase
        .from('jobs')
        .select('total_amount')
        .in('status', ['Scheduled', 'In Progress']);

      // Outstanding Invoices
      const { data: invoices } = await supabase
        .from('invoices')
        .select('total_amount')
        .in('status', ['Draft', 'Sent', 'Overdue']);

      // MTD Revenue (Paid invoices this month)
      const { data: mtdInvoices } = await supabase
        .from('invoices')
        .select('total_amount')
        .eq('status', 'Paid')
        .gte('updated_at', monthStart.toISOString());

      // YTD Revenue (Paid invoices this year)
      const { data: ytdInvoices } = await supabase
        .from('invoices')
        .select('total_amount')
        .eq('status', 'Paid')
        .gte('updated_at', yearStart.toISOString());

      // Monthly trend for last 6 months
      const monthlyTrend = await calculateMonthlyTrend();

      setMetrics({
        pendingEstimates: {
          count: estimates?.length || 0,
          value: estimates?.reduce((sum, e) => sum + parseFloat(e.total_amount || 0), 0) || 0
        },
        activeJobs: {
          count: jobs?.length || 0,
          value: jobs?.reduce((sum, j) => sum + parseFloat(j.total_amount || 0), 0) || 0
        },
        outstandingInvoices: {
          count: invoices?.length || 0,
          value: invoices?.reduce((sum, i) => sum + parseFloat(i.total_amount || 0), 0) || 0
        },
        mtdRevenue: mtdInvoices?.reduce((sum, i) => sum + parseFloat(i.total_amount || 0), 0) || 0,
        ytdRevenue: ytdInvoices?.reduce((sum, i) => sum + parseFloat(i.total_amount || 0), 0) || 0,
        monthlyTrend
      });
    } catch (error) {
      console.error('Error loading metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateMonthlyTrend = async () => {
    const months = [];
    const now = new Date();

    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);

      const { data } = await supabase
        .from('invoices')
        .select('total_amount')
        .eq('status', 'Paid')
        .gte('updated_at', monthDate.toISOString())
        .lt('updated_at', nextMonth.toISOString());

      months.push({
        month: monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        revenue: data?.reduce((sum, i) => sum + parseFloat(i.total_amount || 0), 0) || 0
      });
    }

    return months;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Print Header */}
      <div className="print-only text-center mb-6">
        <h1 className="text-2xl font-bold">JFT Works</h1>
        <h2 className="text-xl">Sales Pipeline Dashboard</h2>
        <p className="text-sm text-gray-600">
          As of {new Date().toLocaleDateString('en-CA')}
        </p>
      </div>

      {/* Screen Header */}
      <div className="no-print mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <TrendingUp size={32} />
          Sales Pipeline Dashboard
        </h1>
        <p className="text-gray-600 mt-2">Real-time business metrics and performance</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Pending Estimates */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Pending Estimates</h3>
            <FileText className="text-blue-500" size={24} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{formatCurrency(metrics.pendingEstimates.value)}</div>
          <p className="text-sm text-gray-500 mt-1">{metrics.pendingEstimates.count} estimates</p>
        </div>

        {/* Active Jobs */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Active Jobs</h3>
            <Briefcase className="text-purple-500" size={24} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{formatCurrency(metrics.activeJobs.value)}</div>
          <p className="text-sm text-gray-500 mt-1">{metrics.activeJobs.count} jobs in progress</p>
        </div>

        {/* Outstanding A/R */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Outstanding A/R</h3>
            <DollarSign className="text-orange-500" size={24} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{formatCurrency(metrics.outstandingInvoices.value)}</div>
          <p className="text-sm text-gray-500 mt-1">{metrics.outstandingInvoices.count} invoices</p>
        </div>
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Month-to-Date Revenue</h3>
            <Calendar className="text-green-500" size={24} />
          </div>
          <div className="text-4xl font-bold text-green-600">{formatCurrency(metrics.mtdRevenue)}</div>
          <p className="text-sm text-gray-500 mt-2">
            {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Year-to-Date Revenue</h3>
            <TrendingUp className="text-blue-500" size={24} />
          </div>
          <div className="text-4xl font-bold text-blue-600">{formatCurrency(metrics.ytdRevenue)}</div>
          <p className="text-sm text-gray-500 mt-2">{new Date().getFullYear()}</p>
        </div>
      </div>

      {/* Monthly Trend */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend (Last 6 Months)</h3>
        <div className="space-y-3">
          {metrics.monthlyTrend.map((month, index) => {
            const maxRevenue = Math.max(...metrics.monthlyTrend.map(m => m.revenue), 1);
            const percentage = (month.revenue / maxRevenue) * 100;

            return (
              <div key={index}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-gray-700">{month.month}</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(month.revenue)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-blue-500 h-3 rounded-full transition-all"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Table */}
      <div className="bg-white rounded-lg shadow-md p-6 mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pipeline Summary</h3>
        <table className="w-full">
          <thead className="border-b-2 border-gray-300">
            <tr>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Stage</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Count</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            <tr>
              <td className="py-3 px-4 text-gray-900">Pending Estimates</td>
              <td className="py-3 px-4 text-right text-gray-900">{metrics.pendingEstimates.count}</td>
              <td className="py-3 px-4 text-right font-semibold text-gray-900">
                {formatCurrency(metrics.pendingEstimates.value)}
              </td>
            </tr>
            <tr>
              <td className="py-3 px-4 text-gray-900">Active Jobs</td>
              <td className="py-3 px-4 text-right text-gray-900">{metrics.activeJobs.count}</td>
              <td className="py-3 px-4 text-right font-semibold text-gray-900">
                {formatCurrency(metrics.activeJobs.value)}
              </td>
            </tr>
            <tr>
              <td className="py-3 px-4 text-gray-900">Outstanding Invoices</td>
              <td className="py-3 px-4 text-right text-gray-900">{metrics.outstandingInvoices.count}</td>
              <td className="py-3 px-4 text-right font-semibold text-gray-900">
                {formatCurrency(metrics.outstandingInvoices.value)}
              </td>
            </tr>
            <tr className="bg-gray-50 font-bold">
              <td className="py-3 px-4 text-gray-900">Total Pipeline</td>
              <td className="py-3 px-4 text-right text-gray-900">
                {metrics.pendingEstimates.count + metrics.activeJobs.count + metrics.outstandingInvoices.count}
              </td>
              <td className="py-3 px-4 text-right text-lg text-gray-900">
                {formatCurrency(
                  metrics.pendingEstimates.value + metrics.activeJobs.value + metrics.outstandingInvoices.value
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          body, html, * {
            background: white !important;
          }
        }
        @media screen {
          .print-only {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SalesPipelineDashboard;
